import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { Link } from 'react-router-dom';
import PostModal from './PostModal';
import PostCard from './PostCard';
import './HomePage.css';
import { DEFAULT_PROFILE_PICTURE } from '../constants/defaults';

// Define the structure for a photo
export interface Photo {
  id: string;
  displayUrl: string;
  thumbnailUrl: string;
  name: string;
  description: string;
}

// Define the structure for the first photo (might be slightly different)
export interface FirstPhoto {
  id: string;
  displayUrl: string;
  thumbnailUrl: string;
  name: string;
  description: string;
}

// Define the structure for a Post
export interface Post {
  id: string;
  title: string;
  content: string;
  location?: string;
  created_at: string;
  visit_date?: string;
  user_id: string;
  user_name: string;
  user_display_name: string;
  user_avatar_url?: string;
  yum_count: number;
  is_yummed?: boolean;
  first_photo?: Photo;
  photos?: Photo[];
  restaurant_name?: string;
  restaurant_address?: string;
  restaurant_primary_type?: string;
}

type ActiveTab = 'all' | 'following';

// Define tab types

const HomePage: React.FC = () => {
  const [allPosts, setAllPosts] = useState<Post[]>([]); // Rename posts to allPosts
  const [followingPosts, setFollowingPosts] = useState<Post[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  // Separate loading states for each tab
  const [loadingStates, setLoadingStates] = useState({ all: true, following: false });
  // Separate error states for each tab (optional, but good practice)
  const [errorStates, setErrorStates] = useState({ all: '', following: '' });
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const { currentUser } = useAuth();
  const [yumLoginErrorPostId, setYumLoginErrorPostId] = useState<string | null>(null); // State for login error

  // --- Fetching Logic ---

  const fetchPosts = useCallback(async (endpoint: string, setter: React.Dispatch<React.SetStateAction<Post[]>>, tab: ActiveTab) => {
    // Set loading state for the specific tab
    setLoadingStates(prev => ({ ...prev, [tab]: true }));
    setErrorStates(prev => ({ ...prev, [tab]: '' })); // Clear previous error for this tab
    try {
      const headers: HeadersInit = {};
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(endpoint + '?limit=20', { // Fetch more posts maybe?
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch posts from ${endpoint}`);
      }
      const data: Post[] = await response.json();
      console.log(`Posts data received from ${endpoint}:`, data);
      const postsWithInitializedData = data.map(post => ({
        ...post,
        photos: post.photos || [], 
        is_yummed: Boolean(post.is_yummed)
      }));
      console.log('Processed posts:', postsWithInitializedData);
      setter(postsWithInitializedData);
    } catch (error) {
      console.error(`Error fetching posts from ${endpoint}:`, error);
      const errorMessage = `Failed to load posts. Please try again later. (${error instanceof Error ? error.message : ''})`;
      setErrorStates(prev => ({ ...prev, [tab]: errorMessage })); // Set error for specific tab
      setter([]); // Clear posts on error
    } finally {
      // Set loading state to false for the specific tab
      setLoadingStates(prev => ({ ...prev, [tab]: false }));
    }
  }, [currentUser]);

  // Effect to fetch posts based on the active tab
  useEffect(() => {
    if (activeTab === 'all') {
      // Fetch only if posts haven't been loaded yet or maybe refetch logic later?
      if (allPosts.length === 0) {
         fetchPosts(API_ENDPOINTS.POSTS, setAllPosts, 'all');
      }
    } else if (activeTab === 'following') {
      if (currentUser) {
        // Fetch only if following posts haven't been loaded yet
        if (followingPosts.length === 0) {
           fetchPosts(`${API_ENDPOINTS.POSTS}/following`, setFollowingPosts, 'following');
        }
      } else {
        // Handle case where user tries to view 'following' but is not logged in
        setFollowingPosts([]);
        setLoadingStates(prev => ({ ...prev, following: false }));
        setErrorStates(prev => ({ ...prev, following: 'Please log in to see posts from users you follow.' }));
      }
    }
    // Dependency array: We only want this effect to re-run when the active tab or current user changes.
    // We don't include allPosts/followingPosts here to avoid re-fetching when they are updated by the fetch itself.
    // fetchPosts is stable due to useCallback.
  }, [activeTab, currentUser, fetchPosts]); // Adjusted dependencies

  // Function to fetch full details for a single post
  const fetchAndShowPostDetails = async (postId: string) => {
    // Prevent fetching if already fetching for this post ID (maybe check if selectedPost matches and has full details?)
    // Or just always fetch? For simplicity, let's always fetch for now.
    // If modal is already open with this post, update it.
    
    // setError(''); // Clear previous errors - maybe keep this? Or handle error display differently

    console.log(`Fetching full details for post ${postId}...`);
    try {
      const headers: HeadersInit = {};
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_ENDPOINTS.POSTS}/${postId}`, {
        headers
      }); 

      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Failed to fetch post details (${response.status}):`, errorData);
        // Update error state, maybe show a toast notification?
        setErrorStates(prev => ({ ...prev, [activeTab]: `Failed to load full post details. Status: ${response.status}` }));
        // Don't close the modal, just show an error indicator inside?
        return; // Stop execution for this function
      }
      
      const fullPostData: Post = await response.json();
      console.log('Full post data received:', fullPostData);
      
      // Ensure the photos array exists, even if empty
      if (!fullPostData.photos) {
        fullPostData.photos = [];
      }
      
      // Update the selected post if it's still the one open
      setSelectedPost(currentSelectedPost => {
        if (currentSelectedPost && currentSelectedPost.id === postId) {
          // Find the post in the current active list to get potentially updated local state
          const currentList = activeTab === 'all' ? allPosts : followingPosts;
          const postInList = currentList.find(p => p.id === postId);
          const localYumState = postInList?.is_yummed ?? currentSelectedPost.is_yummed;
          const localYumCount = postInList?.yum_count ?? currentSelectedPost.yum_count;

          return {
            ...currentSelectedPost, 
            ...fullPostData, 
            is_yummed: localYumState, // *Re-apply* the potentially updated local yum state
            yum_count: localYumCount  // *Re-apply* the potentially updated local yum count
          };
        }
        return currentSelectedPost; // Otherwise, don't change the state (user might have closed modal or selected another)
      });
       setErrorStates(prev => ({ ...prev, [activeTab]: '' })); // Clear error on successful fetch for the active tab

    } catch (error) {
      console.error('Error fetching full post details:', error);
      // Display error to the user (could use a more sophisticated notification inside the modal)
      setErrorStates(prev => ({ ...prev, [activeTab]: `Could not load post details. ${error instanceof Error ? error.message : 'Please try again.'}` }));
      // Potentially update selectedPost state to indicate an error?
    } finally {
      // setIsFetchingPostDetails(false); // Remove this
    }
  };

  // Updated click handler to find post in the correct list
  const handlePostClick = (postId: string, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.action-button')) {
      return;
    }
    
    const currentPosts = activeTab === 'all' ? allPosts : followingPosts;
    const postToShow = currentPosts.find(p => p.id === postId);
    if (postToShow) {
      setSelectedPost(postToShow); // Show modal immediately with basic data
      fetchAndShowPostDetails(postId); // Fetch full details in the background
    } else {
      console.error("Post not found in the active list:", postId);
      setErrorStates(prev => ({ ...prev, [activeTab]: "Could not find the selected post." })); // Show error for active tab
    }
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
    // Clear error only if it was related to fetching details? Maybe keep general errors.
    // Let's clear it for simplicity for now.
    // setError(''); We don't have a single error state anymore
  };

  // Updated Yum click handler to update the correct list
  const handleYumClick = async (postId: string) => {
    if (!currentUser) {
      setYumLoginErrorPostId(postId); 
      return;
    }
    setYumLoginErrorPostId(null);

    const currentListSetter = activeTab === 'all' ? setAllPosts : setFollowingPosts;
    let originalPostState: Post | undefined;

    // Optimistically update UI
    currentListSetter(prevPosts => {
        originalPostState = prevPosts.find(p => p.id === postId);
        return prevPosts.map(post => 
            post.id === postId 
              ? { 
                  ...post, 
                  is_yummed: !post.is_yummed, 
                  yum_count: !post.is_yummed ? (post.yum_count || 0) + 1 : (post.yum_count || 0) - 1 
                }
              : post
          );
        }
    );

    // Also update the selected post if it's the same one
    if (selectedPost?.id === postId) {
      setSelectedPost(prev => 
        prev ? { 
          ...prev, 
          is_yummed: !prev.is_yummed,
          yum_count: !prev.is_yummed ? (prev.yum_count || 0) + 1 : (prev.yum_count || 0) - 1
        } : null
      );
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_ENDPOINTS.POSTS}/${postId}/yum`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
          console.error('Failed to toggle yum status:', response.status, await response.text());
          alert('Failed to toggle yum status.');
          // Revert UI changes
          if (originalPostState) {
            currentListSetter(prevPosts => prevPosts.map(p => p.id === postId ? originalPostState! : p));
             if (selectedPost?.id === postId) {
                setSelectedPost(originalPostState);
             }
          }
      } else {
        const result = await response.json(); 
         console.log('Yum API Result:', result);
         // Update with confirmed data from backend
         currentListSetter(prevPosts => 
            prevPosts.map(post => 
              post.id === postId 
                ? { ...post, is_yummed: result.is_yummed, yum_count: result.yum_count } 
                : post
            )
         );
          if (selectedPost?.id === postId) {
            setSelectedPost(prev => 
              prev ? { ...prev, is_yummed: result.is_yummed, yum_count: result.yum_count } : null
            );
          }
      }
    } catch (error) {
      console.error('Error toggling yum status:', error);
      alert('An error occurred while trying to toggle yum status.');
       // Revert UI changes
      if (originalPostState) {
          currentListSetter(prevPosts => prevPosts.map(p => p.id === postId ? originalPostState! : p));
          if (selectedPost?.id === postId) {
              setSelectedPost(originalPostState);
          }
      }
    }
  };

  // --- Rendering Logic ---

  // Determine which posts to display
  const postsToDisplay = activeTab === 'all' ? allPosts : followingPosts;
  const isLoadingCurrentTab = loadingStates[activeTab];
  const errorCurrentTab = errorStates[activeTab];

  // --- Initial Load / Global Loading ---
  // If *both* lists are empty and the current tab is loading, show the main spinner
  // This handles the very first page load scenario gracefully.
  if (isLoadingCurrentTab && allPosts.length === 0 && followingPosts.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading delicious food posts...</p>
      </div>
    );
  }

  // --- Main Component Structure ---
  return (
    <div className="home-page">
      {/* Tab Navigation - Always visible */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          disabled={loadingStates.all && allPosts.length > 0} // Disable only if actively loading *this* tab's refresh
        >
          All Posts
        </button>
        <button
          className={`tab-button ${activeTab === 'following' ? 'active' : ''}`}
          onClick={() => setActiveTab('following')}
          disabled={!currentUser || (loadingStates.following && followingPosts.length > 0)} // Disable if not logged in OR actively loading *this* tab's refresh
          title={!currentUser ? "Log in to see followed posts" : undefined}
        >
          Following
        </button>
      </div>

      {/* --- Error Display --- */}
      {/* Show error specific to the current tab if it exists */}
      {errorCurrentTab && (
        <div className="error-container main-error">
           <p className="error-message">{errorCurrentTab}</p>
           {/* Optionally add a retry button here that calls fetchPosts for the current tab */}
        </div>
      )}

      {/* Modal - Always potentially available */}
      {selectedPost && (
        <PostModal post={selectedPost} onClose={handleCloseModal} />
      )}

      {/* --- Post Grid Area --- */}
      {/* Show loading indicator *within* the container if loading the active tab, but still show existing posts */}
      {isLoadingCurrentTab && postsToDisplay.length === 0 && !errorCurrentTab && (
         // Show a smaller loading indicator if the tab is loading but the *other* tab had content
         <div className="loading-container-inline"> {/* Use a potentially different style for inline loading */}
           <div className="loading-spinner small"></div> {/* Maybe a smaller spinner */}
           <p>Loading {activeTab === 'following' ? 'posts from users you follow' : 'all posts'}...</p>
         </div>
      )}
      
      {/* Show "No posts" message only if not loading and the list is empty */}
      {!isLoadingCurrentTab && postsToDisplay.length === 0 && !errorCurrentTab ? (
        <div className="no-posts-container">
          <p>
            {activeTab === 'all'
              ? 'No posts yet. Be the first to share!'
              : (currentUser ? 'No posts found from users you follow.' : 'Log in to see posts from users you follow.')}
          </p>
          {/* Link to login only relevant if 'following' tab is active and user not logged in */}
          {activeTab === 'following' && !currentUser && (
            <p>
              <Link to="/login">Log in</Link> to see posts from followed users.
            </p>
          )}
        </div>
      ) : (
        // Render posts if available (even if loading new ones for the active tab)
        <> {/* Use fragment to wrap multiple elements */}
           {/* Optional: Show a subtle loading indicator *over* the posts when refreshing */}
           {isLoadingCurrentTab && postsToDisplay.length > 0 && (
               <div className="loading-overlay"> {/* Style this to overlay subtly */}
                   <div className="loading-spinner small"></div>
               </div>
           )}
          <div className="posts-masonry">
            {postsToDisplay.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onCardClick={handlePostClick}
                onYumClick={handleYumClick}
                currentUser={currentUser}
                yumLoginErrorPostId={yumLoginErrorPostId}
              />
            ))}
          </div>
        </> // Close fragment
      )}
    </div>
  );
};

export default HomePage; 